import { useState } from 'react';
import {
  Modal,
  Upload,
  Button,
  Select,
  Table,
  Alert,
  Space,
  Typography,
  Form,
  Input,
  Tag,
  Checkbox,
  App,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type * as XLSX from 'xlsx';
import type { BomRow } from '@bom/shared';
import { readWorkbook, parseBomFlat } from '../../lib/excel';
import { useCreateBomV2, useReplaceBomRows } from '../../api/boms-v2';
import { useEmailConfig } from '../../api/email-config';
import { useContacts } from '../../api/contacts';
import { usePresignUpload, putFileToS3 } from '../../api/attachments';

const { Dragger } = Upload;

function mimeByName(
  n: string,
):
  | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  | 'application/vnd.ms-excel' {
  return n.toLowerCase().endsWith('.xlsx')
    ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    : 'application/vnd.ms-excel';
}

/** 读 extra 里的原始列（母件编号 / 归属BOM / 子BOMID 等未映射列都在 extra） */
function ex(r: BomRow, key: string): string {
  const v = (r.extra as Record<string, unknown> | undefined)?.[key];
  return typeof v === 'string' && v ? v : '';
}

interface Props {
  tid: string;
  open: boolean;
  onClose: () => void;
  /** create=新建 BOM；replace=覆盖某 BOM 全部行 */
  mode: 'create' | 'replace';
  bomId?: string;
  onDone?: (bomId?: string) => void;
}

export function BomUploadModal({ tid, open, onClose, mode, bomId, onDone }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<{ name: string; code?: string }>();
  const [wb, setWb] = useState<XLSX.WorkBook | null>(null);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [sheet, setSheet] = useState<string | undefined>();
  const [rows, setRows] = useState<BomRow[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [fileObj, setFileObj] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [notify, setNotify] = useState(true);
  const [notifySubject, setNotifySubject] = useState('');
  const [notifyBody, setNotifyBody] = useState('');

  const navigate = useNavigate();
  const create = useCreateBomV2(tid);
  const replace = useReplaceBomRows(tid, bomId ?? '');
  const presign = usePresignUpload(tid);
  const emailCfg = useEmailConfig(tid);
  const configured = !!emailCfg.data?.has_password;
  const contactsQ = useContacts(tid);
  const hasContacts = (contactsQ.data ?? []).some((c) => c.is_active !== false);
  const smtpMissing = notify && !configured;
  const contactsMissing = notify && !hasContacts;
  const blocked = smtpMissing || contactsMissing; // 勾了通知但未配 SMTP / 无联系人 → 拦截
  const pending = create.isPending || replace.isPending || submitting;

  const reset = () => {
    form.resetFields();
    setWb(null);
    setSheetNames([]);
    setSheet(undefined);
    setRows([]);
    setErrors([]);
    setFileName('');
    setFileObj(null);
    setNotify(true);
    setNotifySubject('');
    setNotifyBody('');
  };

  const goConfig = () => {
    reset();
    onClose();
    navigate('/email-config');
  };

  const goContacts = () => {
    reset();
    onClose();
    navigate('/members?tab=contacts');
  };

  const pick = (workbook: XLSX.WorkBook, name: string) => {
    const r = parseBomFlat(workbook, name);
    setRows(r.rows);
    setErrors(r.errors.map((e) => e.reason));
    // 新建时：自动从成品行（层级 0，缺则取首行）带出 BOM 名称/编码，仍可手动改
    if (mode === 'create' && r.rows.length > 0) {
      const top = r.rows.find((row) => row.level_label === '0') ?? r.rows[0]!;
      form.setFieldsValue({
        name: top.name || top.part_no,
        code: top.part_no,
      });
    }
  };

  const handleFile = async (file: File) => {
    try {
      const workbook = await readWorkbook(file);
      setWb(workbook);
      setFileObj(file);
      setFileName(file.name);
      setSheetNames(workbook.SheetNames);
      const first = workbook.SheetNames[0];
      setSheet(first);
      if (first) pick(workbook, first);
    } catch {
      message.error('读取文件失败（.xls/.xlsx）');
    }
    return false;
  };

  const submit = async () => {
    if (rows.length === 0) {
      message.warning('没有可导入的物料行');
      return;
    }
    if (blocked) {
      message.warning('勾选了发送邮件，但发件邮箱或通知联系人未就绪，请先处理或取消勾选');
      return;
    }
    // create 模式先校验名称，失败则不上传
    const v =
      mode === 'create' ? await form.validateFields() : ({} as { name: string; code?: string });
    setSubmitting(true);
    try {
      // 原始文件先传 S3（留档可下载）
      let sourceKey: string | undefined;
      if (fileObj) {
        const ct = mimeByName(fileObj.name);
        const pre = await presign.mutateAsync({
          filename: fileObj.name,
          content_type: ct,
          size_bytes: fileObj.size,
          purpose: 'general',
        });
        await putFileToS3(pre.upload_url, fileObj, ct);
        sourceKey = pre.s3_key;
      }

      if (mode === 'create') {
        const r = await create.mutateAsync({
          name: v.name,
          ...(v.code ? { code: v.code } : {}),
          source_file: fileName,
          ...(sourceKey ? { source_file_s3_key: sourceKey } : {}),
          rows,
          notify,
          ...(notify && notifySubject.trim() ? { notify_subject: notifySubject.trim() } : {}),
          ...(notify && notifyBody.trim() ? { notify_body: notifyBody.trim() } : {}),
        });
        if (notify && r.notify_error)
          message.warning(
            `已创建 BOM（${r.row_count} 行），但通知发送失败：${r.notify_error}（可在详情页「重发通知」）`,
          );
        else
          message.success(
            r.notified
              ? `已创建 BOM 并发送通知（${r.row_count} 行）`
              : `已创建 BOM，导入 ${r.row_count} 行`,
          );
        reset();
        onClose();
        onDone?.(r.id);
      } else {
        const r = await replace.mutateAsync({
          source_file: fileName,
          ...(sourceKey ? { source_file_s3_key: sourceKey } : {}),
          rows,
          notify,
          ...(notify && notifySubject.trim() ? { notify_subject: notifySubject.trim() } : {}),
          ...(notify && notifyBody.trim() ? { notify_body: notifyBody.trim() } : {}),
        });
        if (notify && r.notify_error)
          message.warning(
            `已覆盖更新（${r.row_count} 行），但通知发送失败：${r.notify_error}（可在详情页「重发通知」）`,
          );
        else
          message.success(
            r.notified
              ? `已覆盖更新并发送通知（${r.row_count} 行）`
              : `已覆盖更新，导入 ${r.row_count} 行（旧版本已存快照）`,
          );
        reset();
        onClose();
        onDone?.(bomId);
      }
    } catch (e) {
      message.error((e as Error).message || '导入失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={mode === 'create' ? '上传 BOM（新建）' : '重新上传 BOM（整表覆盖）'}
      open={open}
      width={960}
      onCancel={() => {
        reset();
        onClose();
      }}
      footer={[
        <Button
          key="c"
          onClick={() => {
            reset();
            onClose();
          }}
        >
          取消
        </Button>,
        <Button
          key="ok"
          type="primary"
          loading={pending}
          onClick={submit}
          disabled={rows.length === 0 || blocked}
        >
          {mode === 'create' ? `创建并导入 ${rows.length} 行` : `覆盖导入 ${rows.length} 行`}
        </Button>,
      ]}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {mode === 'create' && (
          <Form form={form} layout="inline">
            <Form.Item
              name="name"
              label="BOM 名称"
              rules={[{ required: true, message: '请输入名称' }]}
            >
              <Input placeholder="如 S1 Ultra 量产BOM" style={{ width: 240 }} />
            </Form.Item>
            <Form.Item name="code" label="编码（可选）">
              <Input placeholder="如 P-0148" style={{ width: 180 }} />
            </Form.Item>
          </Form>
        )}

        {!wb ? (
          <Dragger
            accept=".xls,.xlsx"
            beforeUpload={handleFile}
            showUploadList={false}
            maxCount={1}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">点击或拖拽 BOM .xls / .xlsx 到此处</p>
            <p className="ant-upload-hint">需含「子件编号」列；其余列原样保留</p>
          </Dragger>
        ) : (
          <>
            <Space>
              <Typography.Text>工作表：</Typography.Text>
              <Select
                style={{ width: 240 }}
                value={sheet ?? null}
                onChange={(n) => {
                  setSheet(n);
                  if (wb) pick(wb, n);
                }}
                options={sheetNames.map((n) => ({ value: n, label: n }))}
              />
              <Button size="small" onClick={reset}>
                重新选择
              </Button>
              <Typography.Text type="secondary">{fileName}</Typography.Text>
            </Space>
            {errors.length > 0 && <Alert type="warning" showIcon message={errors.join('；')} />}
            <Space>
              <Typography.Text type="secondary">将导入 {rows.length} 行物料</Typography.Text>
              <Checkbox checked={notify} onChange={(e) => setNotify(e.target.checked)}>
                上传后通知联系人（附原文件）
              </Checkbox>
            </Space>
            {smtpMissing && (
              <Alert
                type="warning"
                showIcon
                message="尚未配置发件邮箱"
                description="勾选“发送邮件”需先配置本产品的 SMTP 发件邮箱；或取消勾选后直接上传。"
                action={
                  <Button size="small" type="primary" onClick={goConfig}>
                    去配置
                  </Button>
                }
              />
            )}
            {contactsMissing && (
              <Alert
                type="warning"
                showIcon
                message="尚未配置通知联系人"
                description="勾选“发送邮件”需先添加本产品的通知联系人；或取消勾选后直接上传。"
                action={
                  <Button size="small" type="primary" onClick={goContacts}>
                    去添加联系人
                  </Button>
                }
              />
            )}
            {notify && !blocked && (
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Input
                  placeholder="邮件主题（留空用默认：项目名称-BOM/ECN 主要修改内容）"
                  value={notifySubject}
                  maxLength={200}
                  onChange={(e) => setNotifySubject(e.target.value)}
                />
                <Input.TextArea
                  placeholder="邮件正文（留空用默认）"
                  value={notifyBody}
                  rows={3}
                  maxLength={5000}
                  onChange={(e) => setNotifyBody(e.target.value)}
                />
              </Space>
            )}
            <Table<BomRow>
              rowKey={(_, i) => String(i)}
              size="small"
              dataSource={rows}
              scroll={{ x: 1280, y: 420 }}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50, 100, 200],
                size: 'small',
              }}
              columns={[
                { title: '料号', dataIndex: 'part_no', width: 130, fixed: 'left' },
                { title: '名称', dataIndex: 'name', width: 150, ellipsis: true },
                { title: '规格', dataIndex: 'spec', width: 220, ellipsis: true },
                { title: '用量', dataIndex: 'qty', width: 60 },
                { title: '单位', dataIndex: 'unit', width: 56 },
                {
                  title: '位号',
                  dataIndex: 'designator',
                  width: 130,
                  ellipsis: true,
                  render: (v?: string) => v || '—',
                },
                {
                  title: '母件编号',
                  width: 120,
                  render: (_: unknown, r: BomRow) => ex(r, '母件编号') || '—',
                },
                {
                  title: '归属BOM',
                  width: 90,
                  render: (_: unknown, r: BomRow) => ex(r, '归属BOM') || '—',
                },
                {
                  title: '子BOM',
                  width: 90,
                  render: (_: unknown, r: BomRow) => {
                    const s = ex(r, '子BOMID');
                    return s && s !== '0' ? s : '—';
                  },
                },
                {
                  title: '层级',
                  dataIndex: 'level_label',
                  width: 64,
                  render: (v?: string) => v ?? '—',
                },
                {
                  title: '状态',
                  dataIndex: 'change_status',
                  width: 72,
                  render: (v?: string) =>
                    v ? <Tag color={v === '增加' ? 'green' : 'orange'}>{v}</Tag> : '—',
                },
                {
                  title: 'ECN编号',
                  dataIndex: 'ecn_no',
                  width: 110,
                  render: (v?: string) => v ?? '—',
                },
              ]}
            />
          </>
        )}
      </Space>
    </Modal>
  );
}
